import {Popover} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useContext, useState, useCallback, useRef, useEffect} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {pathToURL} from 'BenchlingUtil';
import {LLM_CHAT_ANALYTICS_ACTIONS} from 'constants/analytics-products/LLMChatConstants';
import {AgentType, ChatType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ChatContext} from 'llm/chat-library/sidepanel/ChatRouter';
import {getChatTypeText} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import {getChatUrlPath} from 'llm/routes/UrlUtils';
import {getActiveShareLinkItemId} from 'ShareAccess';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import * as Keyboard from 'util/Keyboard';
import {capitalize} from 'util/NameUtil';

import {useFeedbackForm, LLMFeedbackFormContents} from './LLMFeedbackFormContents';

export interface LLMFeedbackChatToolbarProps {
  chatEventId: string;
  chatEventCreatedAt: string;
  agentType?: AgentType;
}

// Feedback is stored in analytics, so it is not persisted in the traditional sense. Instead of querying
// to see if a user has already submitted feedback, we use browser session state to
const FeedbackSubmissionState = makeUseGlobalClientState<{
  submittedReaction: FeedbackReaction | null;
  submittedTextFeedback: boolean;
}>({
  submittedReaction: null,
  submittedTextFeedback: false,
});

/** @package */
export enum FeedbackReaction {
  THUMBS_UP = 'THUMBS_UP',
  THUMBS_DOWN = 'THUMBS_DOWN',
}

function reactionToDisplayText(reaction: FeedbackReaction | null): string {
  switch (reaction) {
    case FeedbackReaction.THUMBS_UP:
      return '👍';
    case FeedbackReaction.THUMBS_DOWN:
      return '👎';
    default:
      return 'Unknown';
  }
}

function trackReactionFeedback(
  reaction: FeedbackReaction,
  chatType: ChatType | null,
  chatId: string | null,
  chatEventId: string,
  agentType: AgentType | null
): void {
  trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.SUBMITTED_LLM_CHAT_REACTION_FEEDBACK, {
    reaction,
    chatType,
    chatId,
    chatEventId,
    agentType,
  });
}

function useFeedbackPopoverToggle(clearForm: () => void): {
  isTextFeedbackOpen: boolean;
  openAndFocusTextFeedback: () => void;
  closeTextFeedback: () => void;
  focusTextArea: () => void;
  textFeedbackRef: React.RefObject<HTMLTextAreaElement>;
} {
  // User must first thumbs up or down the response before they can submit feedback.
  // Use a counter so that we can refocus the text area when the popover is opened.
  const [openCount, setIsTextFeedbackOpen] = useState(0);
  const openAndFocusTextFeedback = useCallback(() => {
    setIsTextFeedbackOpen((x) => x + 1);
  }, []);
  const textFeedbackRef = useRef<HTMLTextAreaElement>(null);

  const closeTextFeedback = useCallback(() => {
    setIsTextFeedbackOpen(0);
    clearForm();
  }, [setIsTextFeedbackOpen, clearForm]);

  const focusTextArea = useCallback(() => {
    if (openCount > 0 && textFeedbackRef.current) {
      textFeedbackRef.current.focus();
    }
  }, [openCount]);

  useEffect(() => {
    focusTextArea();
  }, [
    // Run any time openCount is incremented
    openCount,
    focusTextArea,
  ]);

  return {
    isTextFeedbackOpen: openCount > 0,
    openAndFocusTextFeedback,
    closeTextFeedback,
    focusTextArea,
    textFeedbackRef,
  };
}

const FEEDBACK_REACTION_BUTTON_PROPS: Record<
  FeedbackReaction,
  {
    'aria-label': string;
    icon: BenchlingIconName;
  }
> = {
  [FeedbackReaction.THUMBS_UP]: {'aria-label': 'Yes', icon: 'thumbs-up'},
  [FeedbackReaction.THUMBS_DOWN]: {'aria-label': 'No', icon: 'thumbs-down'},
};

/**
 * A variant of the LLMFeedbackModal that is used in the chat interface.
 *
 * Notable, modals obscure the chat interface, where the user may wish to cite some of the text.
 */
export default function LLMFeedbackChatToolbar(props: LLMFeedbackChatToolbarProps): JSX.Element | null {
  const context = useContext(ChatContext);
  const {chatEventId, chatEventCreatedAt, agentType} = props;

  const [state, setState] = FeedbackSubmissionState.useGlobalClientState(chatEventId);
  const {
    formProps,
    clearForm,
    submitForm,
    disabled: textFeedbackSubmitDisabled,
  } = useFeedbackForm({
    title: `${context ? capitalize(getChatTypeText(context.chatType)) : 'Chat'} feedback`,
    requiredContextItems: [
      {name: 'Reaction', value: reactionToDisplayText(state.submittedReaction)},
      ...(context
        ? [
            {name: 'URL', value: pathToURL(getChatUrlPath(context.chatId ?? 'new'))},
            {name: 'Chat Type', value: context.chatType},
            {name: 'Chat ID', value: context.chatId ?? 'New chat'},
          ]
        : []),
      {name: 'Chat Event ID', value: chatEventId},
      {name: 'Chat Event Timestamp', value: chatEventCreatedAt},
      {name: 'Agent Type', value: agentType ?? 'Unknown'},
    ],
    optionalContextItems: [],
  });

  const {isTextFeedbackOpen, openAndFocusTextFeedback, closeTextFeedback, focusTextArea, textFeedbackRef} =
    useFeedbackPopoverToggle(clearForm);

  const formRef = useRef<HTMLFormElement>(null);
  const handleFormSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault(); // Form submission is handled via below `submitForm`.
      await submitForm();
      setState({submittedTextFeedback: true, submittedReaction: state.submittedReaction});
      closeTextFeedback();
    },
    [submitForm, state.submittedReaction, closeTextFeedback, setState]
  );

  const handleFormKeyDown = useCallback((e: React.KeyboardEvent<HTMLFormElement>) => {
    // Support common UX of letting user submit form when pressing Ctrl+Enter in text area.
    if (Keyboard.isOSCtrlKey(e) && Keyboard.isEnterKey(e)) {
      e.preventDefault();
      formRef.current!.requestSubmit();
    }
  }, []);

  // Render nothing if the user doesn't have write access.
  if (context && getActiveShareLinkItemId() === context.chatId) {
    return null;
  }

  return (
    <div className={css(styles.toolbar)}>
      {[FeedbackReaction.THUMBS_UP, FeedbackReaction.THUMBS_DOWN].map((reaction) => (
        <Button
          key={reaction}
          {...FEEDBACK_REACTION_BUTTON_PROPS[reaction]}
          buttonStyle={
            state.submittedReaction === reaction
              ? ButtonStyle.TERTIARY_BLUE_POWDER
              : ButtonStyle.TERTIARY_WHITE
          }
          size={ButtonSize.SMALL}
          onClick={() => {
            // No-op if the user has clicked this button last. Semantically, marking the button `disabled`
            // would be correct, but it too strongly emphasizes disabled over selected.
            if (state.submittedReaction === reaction) {
              return;
            }
            trackReactionFeedback(
              reaction,
              context?.chatType ?? null,
              context?.chatId ?? null,
              chatEventId,
              agentType ?? null
            );
            setState({submittedTextFeedback: false, submittedReaction: reaction});

            if (reaction === FeedbackReaction.THUMBS_UP) {
              // Unlike thumbs down, we don't auto-open the text feedback popover. However, if the popover is
              // already open, we want to refocus the text area in case the user would like to explain why
              // they gave a thumbs up.
              focusTextArea();
            } else {
              openAndFocusTextFeedback();
            }
          }}
          dataTestAttrs={{component: 'LLMFeedbackChatToolbar', element: 'reactionButton', key: reaction}}
        />
      ))}
      {state.submittedReaction != null && (
        <Popover
          opened={isTextFeedbackOpen}
          closeOnClickOutside={false}
          withArrow={true}
          withinPortal={true}
          arrowSize={20}
          shadow="md"
          width={650}
        >
          <Popover.Target>
            {state.submittedTextFeedback ? (
              <span className="text-success">
                <BenchlingIcon icon="check" /> Feedback sent!
              </span>
            ) : (
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  openAndFocusTextFeedback();
                }}
                {...getTestProps({component: 'LLMFeedbackChatToolbar', element: 'tellUsMoreLink'})}
              >
                Tell us more
              </a>
            )}
          </Popover.Target>
          <Popover.Dropdown>
            <form ref={formRef} onSubmit={handleFormSubmit} onKeyDown={handleFormKeyDown}>
              <LLMFeedbackFormContents
                textAreaRef={textFeedbackRef}
                isPositiveFeedback={state.submittedReaction === FeedbackReaction.THUMBS_UP}
                {...formProps}
              />
              <div className={css(styles.buttonContainer)}>
                <Button
                  text="Cancel"
                  buttonStyle={ButtonStyle.TERTIARY_GRAY}
                  size={ButtonSize.MINI}
                  onClick={closeTextFeedback}
                />
                <Button
                  text="Submit"
                  type="submit"
                  buttonStyle={ButtonStyle.SECONDARY}
                  size={ButtonSize.MINI}
                  disabled={textFeedbackSubmitDisabled}
                />
              </div>
            </form>
          </Popover.Dropdown>
        </Popover>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  textFeedback: {
    maxWidth: '800px',
  },
  toolbar: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    // If we are rendered within a flex container, we will wrap if content doesn't fit.
    // Notably, "Tell us more" will wrap to the next line in unexpanded sidepanel.
    flex: 1,
    flexWrap: 'wrap',
  },
  buttonContainer: {
    marginTop: '8px',
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end',
  },
});
